import { FC, FormEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader, SearchableMultiselect, SearchableMultiselectOption } from 'components';
import { useCustomer } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import { CustomerReportGroup, isReportGroup } from 'types';
import { formatCustomer } from 'utils/format';

import { deleteCustomerReportGroup, getCustomerReportGroupById, updateCustomerReportGroup } from 'services/customer-report-groups';
import EmailSelect from '../customers/email-select';
import HourSelect from '../customers/report-select';
import TimeZoneSelect from '../locations/time-zone';

export const EditReportGroup: FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { customers } = useCustomer();
    const { timeZoneIndicator } = useTimeZone();

    const [reportGroup, setReportGroup] = useState<Partial<CustomerReportGroup>>({});
    const originalReportGroup = useMemo(
        () => getCustomerReportGroupById(Number(params.id)).then(val => val ?? {}),
        [params.id]
    )
    useEffect(
        () => {
            originalReportGroup.then(setReportGroup);
        },
        [originalReportGroup]
    )

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState('');
    const formRef = useRef<HTMLFormElement>(null);

    const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
        if (!!params.id) {
            navigate(`/settings/report-groups/${params.id}`, { replace: true });
            return;
        }
        navigate('/settings/report-groups', { replace: true });
    }, [navigate, params.id]);

    const handleDelete = useCallback(() => {
        deleteCustomerReportGroup(Number(params.id));
        navigate('/settings/report-groups');
    }, [navigate, params.id])

    const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
        async e => {
            e.preventDefault();
            setIsSubmitting(true);
            if (!isReportGroup(reportGroup) || !reportGroup.customers.length) {
                if (!reportGroup.customers?.length) setError('Please select at least one customer');
                setIsSubmitting(false);
                return;
            }

            const response = await updateCustomerReportGroup(reportGroup);

            setIsSubmitting(false);
            navigate(`/settings/report-groups`, {
                replace: true,
                state: { message: !response ? new Error('Create User Failed') : 'Create User Success' }
            });
        },
        [navigate, reportGroup]
    );

    const handleRemoveCustomer = useCallback((id: number) =>
        setReportGroup({
            ...reportGroup,
            customers: [...(reportGroup.customers?.filter(c => c.id !== id) ?? [])]
        }),
        [reportGroup]
    )

    return (
        <Loader loading={!reportGroup}>
            <form className="page" id="new-report-group" onReset={handleReset} onSubmit={handleSubmit} ref={formRef}>
                <div className="page-header">
                    <h1>NEW CUSTOMER REPORT GROUP</h1>
                    <div className="d-flex g-3 flex-row-reverse">
                        <Loader loading={isSubmitting}>
                            <button className="btn-primary" type="submit">
                                SUBMIT
                            </button>
                        </Loader>
                        <button className="btn-danger" onClick={handleDelete} data-cy="customer-report-group-delete-button">
                            DELETE
                        </button>
                        <button className="btn-outline" type="reset">
                            CANCEL
                        </button>
                    </div>
                </div>
                <div className="card-primary">
                    <div className="card-header px-3">
                        <h4>GENERAL INFORMATION</h4>
                    </div>
                    <div className="card-body">
                        <div className="grid-3 p-2">
                            <h6 className="text-info-dark">NAME</h6>
                            <h6 className="text-info-dark">CUSTOMERS</h6>
                            <h6 className='text-info-dark'>TIMEZONE</h6>
                            <input className='margin-right-50pc' value={reportGroup.name} onChange={({ target }) => setReportGroup(rg => ({ ...rg, name: target.value }))} />
                            <div>
                                <div className='margin-right-50pc'>
                                    <SearchableMultiselect
                                        onSelect={(option: SearchableMultiselectOption) => {
                                            if (option.selected) {
                                                setReportGroup({
                                                    ...reportGroup,
                                                    customers: [...(reportGroup.customers ?? []), customers.find(c => c.id === option.id) ?? []].flat()
                                                });
                                            } else {
                                                handleRemoveCustomer(option.id)
                                            }
                                        }}
                                        options={customers.map(({ id, name, location }) => ({
                                            id,
                                            selected: !!reportGroup.customers?.some(c => c.id === id),
                                            value: formatCustomer({ name, location })
                                        }))}
                                        error={error}
                                        data-cy="reports-group-accounts-input"
                                    />
                                </div>
                                {
                                    (reportGroup.customers ?? []).map(({ id, name }) => {
                                        return (
                                            <div className="tag g-2 margin-top-8px margin-right-8px" key={id} data-cy={`new-report-group-account-${id}-tag`}>
                                                <p className="text-primary">{name}</p>
                                                <button type="button" className="tag-close" onClick={() => handleRemoveCustomer(id)}>
                                                    <span className="material-icons">close</span>
                                                </button>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                            <TimeZoneSelect timeZone={reportGroup.timezone} setTimeZone={timezone => setReportGroup(rg => ({ ...rg, timezone }))} />
                        </div>
                        <div className="card-primary">
                            <div className="card-header px-3">
                                <h4 className="text-uppercase">email</h4>
                            </div>
                            <div className="card-body">
                                <div className="p-2 d-flex align-items-center g-2">
                                    <h6 className="text-info-dark mb-0">Send Automated Reports</h6>
                                    <label className="switch mb-0">
                                        <input
                                            type="checkbox"
                                            checked={reportGroup.sendAutomatedReport === true}
                                            onChange={e => setReportGroup(c => c && { ...c, sendAutomatedReport: e.target.checked })}
                                            data-cy="edit-report-group-send-automated-reports-input"
                                        />
                                        <span className="slider round" />
                                    </label>
                                </div>
                                <div className="p-2">
                                    <h6 className="text-info-dark">Email Addresses</h6>
                                    <EmailSelect
                                        email={reportGroup?.email}
                                        setEmail={email => setReportGroup(c => c && { ...c, email })}
                                    />
                                </div>
                                <div className="p-2">
                                    <h6 className="text-info-dark">Activity Report ({timeZoneIndicator})</h6>
                                    <HourSelect
                                        id="activity-report-hours"
                                        hours={reportGroup.activityReportHours ?? []}
                                        setHours={activityReportHours => setReportGroup(c => c && { ...c, activityReportHours })}
                                    />
                                </div>
                                <div className="p-2">
                                    <h6 className="text-info-dark">Status Report ({timeZoneIndicator})</h6>
                                    <HourSelect
                                        id="status-report-hours"
                                        hours={reportGroup.statusReportHours ?? []}
                                        setHours={statusReportHours => setReportGroup(c => c && { ...c, statusReportHours })}
                                    />
                                </div>
                                <div className="p-2">
                                    <h6 className="text-info-dark">Inventory Report ({timeZoneIndicator})</h6>
                                    <HourSelect
                                        id="inventory-report-hours"
                                        hours={reportGroup.inventoryReportHours ?? []}
                                        setHours={inventoryReportHours => setReportGroup(c => c && { ...c, inventoryReportHours })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Loader>
    );
};